"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useAuthState } from "@/hooks/use-auth-state";
import {
  authAttributionProperties,
  clearAuthAttribution,
  readAuthAttribution,
} from "@/lib/analytics/auth-attribution";
import {
  captureClientEvent,
  captureIdentifiedClientEvent,
  getIdentifiedAnalyticsUserId,
  identifyAnalyticsUser,
  registerTelemetryContext,
  resetAnalyticsUser,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { resolveIdentityAction } from "@/lib/analytics/identity-lifecycle";
import { isClientTelemetryDeliveryEnabled } from "@/lib/telemetry/environment";

/**
 * An account created within this window of the sign-in completing is a signup,
 * not a returning login. Generous enough to cover an OTP round trip or an OAuth
 * consent screen, short enough that a member logging in a day later is never
 * mistaken for a new one.
 */
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;

import "../../../instrumentation-client";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Initialization is handled in instrumentation-client.ts
  if (!isClientTelemetryDeliveryEnabled()) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <PostHogAuthWrapper>{children}</PostHogAuthWrapper>
    </PHProvider>
  );
}

function PostHogAuthWrapper({ children }: { children: React.ReactNode }) {
  const { authStatus, identity } = useAuthState();
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // `PostHog` persistence survives a document load, while this ref does not.
    // That distinction gives us one authoritative authenticated-visit event per
    // document without emitting again when profile data refetches in the same
    // document.
    const shouldCaptureAuthenticatedVisit =
      Boolean(identity?.id) && identifiedUserIdRef.current !== identity?.id;

    // The whole decision, made in one testable place — see
    // `lib/analytics/identity-lifecycle.ts` for why reset is so narrow.
    const action = resolveIdentityAction({
      status: authStatus,
      userId: identity?.id ?? null,
      // The ref covers in-document transitions; PostHog persistence covers a
      // full-page OAuth return that replaced account A with account B before
      // React ever rendered an unauthenticated state.
      identifiedUserId:
        identifiedUserIdRef.current ?? getIdentifiedAnalyticsUserId(),
    });

    if (action.kind === "noop") return;

    if (action.kind === "reset") {
      identifiedUserIdRef.current = null;
      resetAnalyticsUser();
      // Reset wipes every registered super property. The next anonymous event
      // must still say which deployment and which surface it came from.
      registerTelemetryContext();
      return;
    }

    if (action.kind === "switch") {
      // Never alias two authenticated database users. A new anonymous boundary
      // preserves device continuity while keeping both people's histories
      // separate on a shared browser.
      identifiedUserIdRef.current = null;
      resetAnalyticsUser();
      registerTelemetryContext();
    }

    if (!identity) return;

    // `identity.email` is the verified CONTACT address (getCurrentSessionIdentity
    // resolves it), so it is null for a phone-only account rather than a
    // `.invalid` placeholder. Sending the placeholder would have created a
    // permanent person property that looks like an email, is not one, and
    // cannot be corrected once PostHog has it.
    identifyAnalyticsUser(identity.id, identity.analyticsProperties);

    if (action.kind === "switch") {
      // Attribute the boundary to the newly active account without recording
      // the previous database user ID as an event property. The reset above is
      // what prevents PostHog from joining the two people's histories.
      captureClientEvent(ANALYTICS_EVENTS.AUTH_ACCOUNT_SWITCHED, {
        reset_before_identify: true,
      });
    }

    if (!shouldCaptureAuthenticatedVisit) return;

    identifiedUserIdRef.current = identity.id;
    captureClientEvent(ANALYTICS_EVENTS.AUTH_SESSION_IDENTIFIED, {
      has_email: Boolean(identity.email),
      has_name: Boolean(identity.name),
      identity_was_already_persisted:
        action.kind === "identify" && !action.isNewIdentity,
    });

    /**
     * The one place that sees every completed sign-in.
     *
     * Not the drawer, and not the buttons: OAuth leaves the document, phone
     * OTP stays on it, and native does neither. Each path had its own partial
     * notion of "done", which is why the auth half of the checkout funnel only
     * ever reported the subset that happened to replay a stashed basket. Here
     * the session simply exists, however it got here.
     */
    const attribution = readAuthAttribution();
    if (attribution) {
      const createdAt = identity.createdAt
        ? new Date(identity.createdAt).getTime()
        : null;
      captureIdentifiedClientEvent(
        ANALYTICS_EVENTS.CHECKOUT_AUTH_COMPLETED,
        identity.id,
        {
          ...authAttributionProperties(),
          is_new_account:
            createdAt !== null &&
            Date.now() - createdAt < NEW_ACCOUNT_WINDOW_MS,
        },
      );
      // One completion per prompt. Leaving it would re-attribute the next
      // sign-in in this tab to a purchase that already finished.
      clearAuthAttribution();
    }
  }, [authStatus, identity]);

  return <>{children}</>;
}
